import { CopyButton } from "@/components/ui/copy-button";
import type { BankTransferDetails } from "@/lib/bank-transfer";
import { SUPPORT_EMAIL } from "@/lib/support";
import { cn } from "@repo/design-system/utils";
import { Buildings } from "@medusajs/icons";
import { memo } from "react";

/**
 * One label/value pair, with an optional copy affordance.
 *
 * Stacks on narrow screens and sits on one line from `sm` up, so a long account
 * name never squeezes the value column on a phone.
 */
const DetailRow = memo(function DetailRow({
  label,
  value,
  copyable = false,
  emphasis = false
}: {
  label: string;
  value: string;
  /** Whether the value can be copied straight into a banking app. */
  copyable?: boolean;
  emphasis?: boolean;
}) {
  return (
    <div className="flex flex-col gap-0.5 py-1.5 sm:flex-row sm:items-center sm:justify-between sm:gap-4">
      <dt className="text-xs text-gray-500 sm:text-sm">{label}</dt>
      <dd className="flex items-center gap-1">
        <span
          className={cn(
            "font-medium text-gray-900",
            emphasis ? "text-base tracking-wide" : "text-sm"
          )}
        >
          {value}
        </span>
        {copyable && <CopyButton value={value} label={label} />}
      </dd>
    </div>
  );
});

export interface BankTransferInstructionsProps {
  /**
   * The marketplace's payee details, or null when the payment session carried
   * none - a backend misconfiguration rather than a normal state. The panel
   * then drops the details list *and* the copy that points at it, and tells the
   * buyer not to transfer, rather than sending them to an account it can't name.
   */
  details: BankTransferDetails | null;
  /**
   * How many orders this transfer covers. A multi-seller checkout completes one
   * order per cart, so the reference instruction has to be plural there - the
   * confirmation page lists every order number, not one.
   *
   * Defaults to 1 for the single-order surfaces (order confirmation, profile).
   */
  orderCount?: number;
  className?: string;
}

/**
 * How to pay by bank transfer, plus the payee details to pay to.
 *
 * Written to be shared: the checkout payment step renders it before the order
 * exists (KAN-46), and the order confirmation and profile views will render the
 * same panel afterwards (KAN-15 points 3 and 4).
 *
 * PayID leads when configured because it is the faster path for the salon, but
 * BSB and account number stay visible beside it - not every salon's banking app
 * has PayID set up, and dropping the account details would strand those.
 */
export const BankTransferInstructions = memo(function BankTransferInstructions({
  details,
  orderCount = 1,
  className
}: BankTransferInstructionsProps) {
  return (
    <div
      className={cn(
        "rounded-lg border border-gray-200 bg-gray-50 px-4 py-3",
        className
      )}
    >
      <div className="flex items-center gap-2 text-sm font-medium text-gray-900">
        <Buildings aria-hidden="true" />
        <span>Pay by bank transfer</span>
      </div>

      {details ? (
        <p className="mt-1.5 text-sm text-gray-600">
          {orderCount > 1
            ? `Place your order first, then transfer each order's total using the details below. Use that order's number as the payment reference - all ${orderCount} are shown on the next screen.`
            : "Place your order first, then transfer the total using the details below. Use your order number as the payment reference - it is shown on the next screen."}
        </p>
      ) : (
        <p className="mt-1.5 text-sm text-red-700">
          We couldn&apos;t load the payee details for this transfer. Please
          contact{" "}
          <a
            href={`mailto:${SUPPORT_EMAIL}`}
            className="font-medium underline underline-offset-2"
          >
            {SUPPORT_EMAIL}
          </a>{" "}
          before sending any payment, or choose another payment method.
        </p>
      )}

      {details && (
        <dl className="mt-3 divide-y divide-gray-200 border-t border-gray-200 pt-1">
          {details.payid && (
            <DetailRow label="PayID" value={details.payid} copyable emphasis />
          )}
          <DetailRow
            label="Account name"
            value={details.accountName}
            copyable
          />
          <DetailRow label="BSB" value={details.bsb} copyable />
          <DetailRow
            label="Account number"
            value={details.accountNumber}
            copyable
          />
          <DetailRow label="Bank" value={details.bankName} />
        </dl>
      )}
    </div>
  );
});
